import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {useState} from 'react';

import preferences from 'Preferences';
import {Header1, Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';

interface FeatureBannerProps {
  children: JSX.Element;
  className?: string;
  seenBannerPreference?: string;
}

function FeatureBanner(props: FeatureBannerProps): JSX.Element | null {
  const [visible, setVisible] = useState(
    Boolean(props.seenBannerPreference == null || !preferences.get(props.seenBannerPreference))
  );

  function handleDismiss(): void {
    if (props.seenBannerPreference) {
      preferences.set(props.seenBannerPreference, true);
    }
    setVisible(false);
  }

  if (!visible) {
    return null;
  }

  return (
    <nyc.Row>
      <div className={classNames('featureBanner', props.className)}>
        {props.children}
        {props.seenBannerPreference && (
          <BenchlingIcon className="featureBanner-remove" icon="close" onClick={handleDismiss} />
        )}
      </div>
    </nyc.Row>
  );
}

FeatureBanner.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  seenBannerPreference: PropTypes.string,
};

interface FeatureBannerItemProps {
  imageSource?: string;
  oversized: boolean;
  stepNumber?: number;
  subtitle: string;
  title: string;
}

export function FeatureBannerItem({
  imageSource,
  stepNumber,
  title,
  subtitle,
  oversized,
}: FeatureBannerItemProps): JSX.Element {
  return (
    <div className={classNames('featureBanner-item', {'featureBanner-title': oversized})}>
      {imageSource && (
        <img
          src={imageSource}
          className={classNames('featureBanner-icon', {'featureBanner-icon--large': oversized})}
        />
      )}
      {stepNumber && (
        <div className="featureBanner-stepNumber featureBanner-stepNumber--large">{stepNumber}</div>
      )}
      <div>
        {oversized ? <Header1>{title}</Header1> : <Title>{title}</Title>}
        <div className="featureBanner-subtitle">{subtitle}</div>
      </div>
    </div>
  );
}

FeatureBannerItem.propTypes = {
  imageSource: PropTypes.string,
  oversized: PropTypes.bool.isRequired,
  stepNumber: (props: FeatureBannerItemProps, ...args: [string, string, string, string]) => {
    const numberError = PropTypes.number(props, ...args);
    if (numberError) {
      return numberError;
    }
    if (props.stepNumber && props.imageSource) {
      return new Error('Both stepNumber and imageSource specified');
    }
    return null;
  },
  subtitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default FeatureBanner;
